import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    Button,
    CircularProgress,
    downloadFile,
    FileInput,
    platformContext,
    serverError
} from "shared";
import { CdocFormat, constructDocumentPayload, documentService, IDocumentPayloadProps } from "entities/document";
import { ComponentTypes, ITemplate, ITemplateComponent, ITemplateSaveAndTicketComponent, ITemplateTrackerIntegrationComponent } from "entities/template";
import { authContext, propertiesContext } from "entities/user";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import cn from "classnames";
import { Platform } from "@yandex-int/hr-components/lib";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { buildFieldVisibilityContext } from "../../../../lib/utils/buildFieldVisibilityContext";
import { parseTicketResponse } from "../../../../lib/utils/ticket_integration";
import { setLocalStorageFormRecord } from "../../../../lib/utils/record_localstorage";
import { scrollToErrorFields } from "../../../../lib/utils/scrollToError";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_save_and_ticket.module.scss";

interface IDocumentFormSaveAndTicketProps {
    component: ITemplateSaveAndTicketComponent;
    templateComponents: ITemplateComponent[];
    template: ITemplate;
    onValidationCheck: () => Promise<boolean>;
    formRecord: Record<string, any>;
    tooltip: IComponentTooltipSettings;
    enLanguageEnabled: boolean;
}

export const DocumentFormSaveAndTicket: React.FC<IDocumentFormSaveAndTicketProps> = React.memo(({
                                                                                                    component,
                                                                                                    templateComponents,
                                                                                                    formRecord,
                                                                                                    template,
                                                                                                    onValidationCheck,
                                                                                                    tooltip,
                                                                                                    enLanguageEnabled,
                                                                                                }) => {
    const t = useComponentsTranslations();
    const [disabled, setDisabled] = useState<boolean>();
    const [activeBtn, setActiveBtn] = useState<string>();
    const [files, setFiles] = useState<File[]>([]);
    const { i18n } = useTranslation();
    const { extUserId } = useParams();
    const userInfo = useContext(authContext);
    const { getProperty } = useContext(propertiesContext);
    const platform = useContext(platformContext);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    const isExternal = useMemo(() => {
        return !!userInfo && userInfo?.isExternal === true;
    }, [userInfo]);

    const externalFormUserId = useMemo(() => {
        return !!userInfo && userInfo?.isExternal === true ? extUserId : undefined;
    }, [userInfo, extUserId]);

    const fieldVisibilityContext = useMemo(
        () => buildFieldVisibilityContext(
            formRecord,
            templateComponents,
            template,
            enLanguageEnabled,
            i18n.language,
            isExternal,
        ),
        [formRecord, templateComponents, template, enLanguageEnabled, i18n.language, isExternal],
    );

    const isDocPdf = useMemo(() => {
        return component.isDocPdf !== false;
    }, [component]);

    const isDocRtf = useMemo(() => {
        return component.isDocRtf !== false;
    }, [component]);

    const isTicketPdf = useMemo(() => {
        return component.isTicketPdf !== false;
    }, [component]);

    const isTicketRtf = useMemo(() => {
        return component.isTicketRtf !== false;
    }, [component]);

    const showIsNoPass = useMemo(() => {
        return component.isProtected && (component.isDocPdfNopass || component.isDocRtfNopass || component.isTicketPdfNopass || component.isTicketRtfNopass);
    }, [component]);

    const isCreateTicket = useMemo(() => {
        return isTicketRtf ||
            isTicketPdf ||
            component.isTicketPdfNopass ||
            component.isTicketRtfNopass ||
            component.isDocAndTicketPdf ||
            component.isDocAndTicketRtf ||
            component.isTicketPdfRtfNopass ||
            component.isUploadFile;
    }, [component, isTicketPdf, isTicketRtf]);

    const isSaveDocument = useMemo(() => {
        return isDocPdf ||
            isDocRtf ||
            component.isDocPdfNopass ||
            component.isDocRtfNopass ||
            component.isDocAndTicketPdf ||
            component.isDocAndTicketRtf ||
            component.isTicketPdfRtfNopass;
    }, [component, isDocPdf, isDocRtf]);

    const getRecordWithTrackerIntegration = useCallback((record: Record<string, any>) => {
        const updatedRecord: Record<string, any> = { ...record };
        const trackerIntegrationComponents = templateComponents.filter((c) => c.type === ComponentTypes.trackerIntegration) as ITemplateTrackerIntegrationComponent[];

        const startrekIntegration = trackerIntegrationComponents
            .filter((trackerComponent) => trackerComponent.startrekFields?.id)
            .map((trackerComponent) => `${trackerComponent.startrekFields?.id},${trackerComponent.textWithVariables}`);

        if (startrekIntegration.length > 0) {
            updatedRecord.startrekIntegration = startrekIntegration;
        } else {
            delete updatedRecord.startrekIntegration;
        }

        return updatedRecord;
    }, [templateComponents]);

    const getRecordWithTicketData = useCallback((record: Record<string, any>) => {
        return {
            ...getRecordWithTrackerIntegration(record),
            startrekQueue: component.startrekQueue,
            startrekIssueType: component.issueType
        };
    }, [component.issueType, component.startrekQueue, getRecordWithTrackerIntegration]);


    const handleClearEvent = () => {
        setFiles([]);
    };

    const onSaveDocument = (xxya_cdoc_format: CdocFormat, xxya_cdoc_isnopass: boolean, button: string) => {
        const recordForRequest = getRecordWithTrackerIntegration(formRecord);
        const documentPayload = constructDocumentPayload(template, recordForRequest, xxya_cdoc_format, xxya_cdoc_isnopass, i18n.language, externalFormUserId, undefined, undefined, templateComponents, fieldVisibilityContext);
        onValidationCheck().then((isValid) => {
            if (isValid) {
                setDisabled(true);
                setActiveBtn(button);
                setLocalStorageFormRecord(template.id, recordForRequest);
                documentService.downloadDocument(documentPayload).then(res => {
                    AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
                    downloadFile(res.blob, documentPayload.fileName, xxya_cdoc_format === "pdf" ? "pdf" : "docx", res.file, res.filename);
                }).catch(e => {
                    console.error(e);
                    toast.error(serverError(t("serverError"), e));
                }).finally(() => {
                    setDisabled(false);
                    setActiveBtn(undefined);
                });
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        });
    };

    const createTicket = (documentPayload: IDocumentPayloadProps, button: string) => {
        onValidationCheck().then((isValid) => {
            if (isValid) {
                setDisabled(true);
                setActiveBtn(button);
                setLocalStorageFormRecord(template.id, getRecordWithTrackerIntegration(formRecord));
                documentService.createTicket(documentPayload).then(response => {
                    const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"), t("createTicketError"));
                    AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
                    if (parsedResponse.error) {
                        toast.error(parsedResponse.message);
                    } else {
                        toast.success(parsedResponse.message);
                    }
                }).catch(e => {
                    console.log(e);
                    toast.error(serverError(t("serverError"), e));
                }).finally(() => {
                    setDisabled(false);
                    setActiveBtn(undefined);
                });
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        });
    };

    const onCreateTicket = (xxya_cdoc_format: CdocFormat, xxya_cdoc_isnopass: boolean, button: string) => {
        const recordForRequest = getRecordWithTicketData(formRecord);
        const documentPayload = constructDocumentPayload(template, recordForRequest, xxya_cdoc_format, xxya_cdoc_isnopass, i18n.language, externalFormUserId, undefined, undefined, templateComponents, fieldVisibilityContext);

        createTicket(documentPayload, button);
    };

    const onCreateTicketAndSendFiles = () => {
        const recordForRequest = getRecordWithTicketData(formRecord);
        const documentPayload = constructDocumentPayload(template, recordForRequest, CdocFormat.file, undefined, i18n.language, externalFormUserId, files, "xxcdoc_SaveAndTicket_file", templateComponents, fieldVisibilityContext);

        createTicket(documentPayload, "isUploadFile");
    };

    const onCreateTicketPdfAndAttachRtf = () => {
        const recordForRequest = getRecordWithTicketData(formRecord);
        const pdfPayload = constructDocumentPayload(template, recordForRequest, CdocFormat.pdf, false, i18n.language, externalFormUserId, undefined, undefined, templateComponents, fieldVisibilityContext);

        onValidationCheck().then((isValid) => {
            if (isValid) {
                setDisabled(true);
                setActiveBtn("isTicketPdfRtfNopass");
                setLocalStorageFormRecord(template.id, getRecordWithTrackerIntegration(formRecord));
                documentService.createTicket(pdfPayload).then(response => {
                    const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"), t("createTicketError"));
                    if (parsedResponse.error) {
                        toast.error(parsedResponse.message);
                    } else if (!parsedResponse.ticket) {
                        toast.error(t("createTicketError"));
                    } else {
                        AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
                        toast.success(parsedResponse.message);
                        const documentPayload = {
                            ...pdfPayload,
                            xxya_cdoc_format: CdocFormat.rtf,
                            xxya_cdoc_isnopass: true,
                            startrekQueueAttach: component.startrekQueue ? component.startrekQueue : "",
                            startrekAttachDoc: parsedResponse.ticket,
                            startrekComment: t("alsoAttachedDocument")
                        };
                        return documentService.attach(documentPayload).then(response => {
                            const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"));
                            if (parsedResponse.error) {
                                toast.error(parsedResponse.message);
                            } else {
                                toast.success(parsedResponse.message);
                            }
                        });
                    }
                }).catch(e => {
                    console.log(e);
                    toast.error(serverError(t("serverError"), e));
                }).finally(() => {
                    setDisabled(false);
                    setActiveBtn(undefined);
                });
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        });
    };

    const onCreateTicketAndSaveDocument = (xxya_cdoc_format: CdocFormat, activeBtn: string) => {
        const recordForRequest = getRecordWithTicketData(formRecord);
        const documentPayload = constructDocumentPayload(template, recordForRequest, xxya_cdoc_format, undefined, i18n.language, externalFormUserId, undefined, undefined, templateComponents, fieldVisibilityContext);

        onValidationCheck().then((isValid) => {
            if (isValid) {
                setDisabled(true);
                setActiveBtn(activeBtn);
                setLocalStorageFormRecord(template.id, getRecordWithTrackerIntegration(formRecord));
                documentService.createTicket(documentPayload).then(response => {
                    const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"), t("createTicketError"));
                    if (parsedResponse.error) {
                        toast.error(parsedResponse.message);
                        return;
                    }
                    if (!parsedResponse.ticket) {
                        toast.error(t("createTicketError"));
                        return;
                    }
                    AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
                    toast.success(parsedResponse.message);
                    return documentService.downloadDocument(documentPayload).then(res => {
                        downloadFile(res.blob, documentPayload.fileName, xxya_cdoc_format === "pdf" ? "pdf" : "docx", res.file, res.filename);
                    });
                }).catch(e => {
                    console.log(e);
                    toast.error(serverError(t("serverError"), e));
                }).finally(() => {
                    setDisabled(false);
                    setActiveBtn(undefined);
                });
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        });
    };

    return (isCreateTicket || isSaveDocument) ? <div className={!isExternal ? styles.componentContainer : ""}>
        <div className={styles.titleContainer}>
            {!isExternal ? <>
                {isCreateTicket && isSaveDocument && <div
                    className={styles.title}>{t("saveAndTicketLabelInTheQueue", { queue: component.startrekQueue ? component.startrekQueue : "" })}</div>}
                {isSaveDocument && !isCreateTicket && <div
                    className={styles.title}>{t("saveDocument")}</div>}
                {isCreateTicket && !isSaveDocument && <div
                    className={styles.title}>{t("createTicketInQueue", { queue: component.startrekQueue ? component.startrekQueue : "" })}</div>}
            </> : <div></div>}
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            <div className={styles.innerContainer}>
                {(component.isProtected && (isDocPdf || isDocRtf || isTicketPdf || isTicketRtf)) &&
                    <div
                        id="div_document_protected"
                        className={styles.subTitle}>{t("documentProtected")}</div>}
                {(isDocPdf || isDocRtf) && <div
                    className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                    {isDocPdf && <Button className={styles.button}
                                         id="save_document_pdf_btn"
                                         disabled={disabled}
                                         view="legal"
                                         onClick={() => onSaveDocument(CdocFormat.pdf, !component.isProtected, "isDocPdf")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("savePdf")}
                            {activeBtn === "isDocPdf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                    {isDocRtf && <Button className={styles.button}
                                         id="save_document_rtf_btn"
                                         view="legal"
                                         disabled={disabled}
                                         onClick={() => onSaveDocument(CdocFormat.rtf, !component.isProtected, "isDocRtf")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("saveDocument")}
                            {activeBtn === "isDocRtf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                </div>}
                {(isTicketPdf || isTicketRtf) && <div
                    className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                    {isTicketPdf && <Button className={styles.button}
                                            id="save_ticket_pdf_btn"
                                            view="legal"
                                            disabled={disabled}
                                            onClick={() => onCreateTicket(CdocFormat.pdf, !component.isProtected, "isTicketPdf")}>
                        <div className={styles.buttonInnerContainer}>
                            {isExternal ? t("sendPdf") : t("ticketWithPdf")}
                            {activeBtn === "isTicketPdf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                    {isTicketRtf && <Button className={styles.button}
                                            id="save_ticket_rtf_btn"
                                            view="legal"
                                            disabled={disabled}
                                            onClick={() => onCreateTicket(CdocFormat.rtf, !component.isProtected, "isTicketRtf")}>
                        <div className={styles.buttonInnerContainer}>
                            {isExternal ? t("sendDocument") : t("ticketWithDocument")}
                            {activeBtn === "isTicketRtf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                </div>}

                {showIsNoPass && <>
                    <div id="div_document_no_password"
                         className={styles.subTitle}>{t("documentWithoutPassword")}</div>
                    {(component.isDocPdfNopass || component.isDocRtfNopass) && <div
                        className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                        {component.isDocPdfNopass && <Button className={styles.button}
                                                             id="save_document_pdf_no_pass_btn"
                                                             view="legal"
                                                             disabled={disabled}
                                                             onClick={() => onSaveDocument(CdocFormat.pdf, true, "isDocPdfNopass")}>
                            <div className={styles.buttonInnerContainer}>
                                {t("savePdf")}
                                {activeBtn === "isDocPdfNopass" &&
                                    <CircularProgress size={15} className={styles.loader}/>}
                            </div>
                        </Button>}
                        {component.isDocRtfNopass && <Button className={styles.button}
                                                             id="save_document_rtf_no_pass_btn"
                                                             view="legal"
                                                             disabled={disabled}
                                                             onClick={() => onSaveDocument(CdocFormat.rtf, true, "isDocRtfNopass")}>
                            <div className={styles.buttonInnerContainer}>
                                {t("saveDocument")}
                                {activeBtn === "isDocRtfNopass" &&
                                    <CircularProgress size={15} className={styles.loader}/>}
                            </div>
                        </Button>}
                    </div>}
                    {(component.isTicketPdfNopass || component.isTicketRtfNopass) &&
                        <div
                            className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                            {component.isTicketPdfNopass && <Button className={styles.button}
                                                                    id="save_ticket_pdf_no_pass_btn"
                                                                    view="legal"
                                                                    disabled={disabled}
                                                                    onClick={() => onCreateTicket(CdocFormat.pdf, true, "isTicketPdfNopass")}>
                                <div className={styles.buttonInnerContainer}>
                                    {isExternal ? t("sendPdf") : t("ticketWithPdf")}
                                    {activeBtn === "isTicketPdfNopass" &&
                                        <CircularProgress size={15} className={styles.loader}/>}
                                </div>
                            </Button>}
                            {component.isTicketRtfNopass && <Button className={styles.button}
                                                                    id="save_ticket_rtf_no_pass_btn"
                                                                    view="legal"
                                                                    disabled={disabled}
                                                                    onClick={() => onCreateTicket(CdocFormat.rtf, true, "isTicketRtfNopass")}>
                                <div className={styles.buttonInnerContainer}>
                                    {isExternal ? t("sendDocument") : t("ticketWithDocument")}
                                    {activeBtn === "isTicketRtfNopass" &&
                                        <CircularProgress size={15} className={styles.loader}/>}
                                </div>
                            </Button>}
                        </div>}
                </>}
                {component.isDocAndTicketPdf &&
                    <div
                        className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                        <Button className={styles.button}
                                id="save_doc_and_ticket_pdf_btn"
                                view="legal"
                                disabled={disabled}
                                onClick={() => onCreateTicketAndSaveDocument(CdocFormat.pdf, "isDocAndTicketPdf")}>
                            <div className={styles.buttonInnerContainer}>
                                {isExternal ? t("sendAndSavePdf") : t("createTicketAndSavePdfFile")}
                                {activeBtn === "isDocAndTicketPdf" &&
                                    <CircularProgress size={15} className={styles.loader}/>}
                            </div>
                        </Button>
                    </div>}
                {component.isDocAndTicketRtf &&
                    <div
                        className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                        <Button className={styles.button}
                                id="save_doc_and_ticket_rtf_btn"
                                view="legal"
                                disabled={disabled}
                                onClick={() => onCreateTicketAndSaveDocument(CdocFormat.rtf, "isDocAndTicketRtf")}>
                            <div className={styles.buttonInnerContainer}>
                                {isExternal ? t("sendAndSaveDocument") : t("createTicketAndSaveDocument")}
                                {activeBtn === "isDocAndTicketRtf" &&
                                    <CircularProgress size={15} className={styles.loader}/>}
                            </div>
                        </Button>
                    </div>}
                {component.isTicketPdfRtfNopass &&
                    <div
                        className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                        <Button className={styles.button}
                                id="save_ticket_pdf_rtf_btn"
                                view="legal"
                                disabled={disabled}
                                onClick={() => onCreateTicketPdfAndAttachRtf()}>
                            <div className={styles.buttonInnerContainer}>
                                {isExternal ? t("send") : t("createTicketPdfWord")}
                                {activeBtn === "isTicketPdfRtfNopass" &&
                                    <CircularProgress size={15} className={styles.loader}/>}
                            </div>
                        </Button>
                    </div>}
                {component.isUploadFile && <div className={styles.fileContainer}>
                    <div id="signed_document_div">{t("signedDocumentTitle")}</div>
                    <FileInput
                        buttonText={t("chooseFiles")}
                        multiple={true}
                        initialFiles={files}
                        onFilesChange={setFiles}
                        description={<div className={styles.fileInputDescription}>
                            <div className={styles.fileInputDescriptionLine}>{t("toSendDocument")}</div>
                            <div className={styles.fileInputDescriptionLine}>{t("toSendDocumentFirst")}</div>
                            <div className={styles.fileInputDescriptionLine}>{t("toSendDocumentSecond")}</div>
                            <div className={styles.fileInputDescriptionLine}>{t("toSendDocumentThird")}</div>
                        </div>}
                    />
                    <Button className={styles.button}
                            id="create_ticket_and_send_files_btn"
                            view="legal"
                            disabled={disabled || files.length === 0}
                            onClick={() => onCreateTicketAndSendFiles()}>
                        <div className={styles.buttonInnerContainer}>
                            {t("send")}
                            {activeBtn === "isUploadFile" &&
                                <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>
                </div>}
            </div>
        </div>
    </div> : null;
});
